import type { SinalDaVoz } from './orbe/sinal';

/**
 * O sinal que dirige o orbe, lido dos analisadores que a sessão de voz já tem: o do
 * microfone (`medidor`) e o da saída da voz da VecchIA (o áudio que toca de verdade). Nível
 * pelo volume em dB; graves, médios e agudos pela energia de cada faixa. Sem alocar a cada
 * quadro: os vetores ficam na `memoria`.
 */

const FAIXAS = {
    graves: [60, 250],
    medios: [250, 2000],
    agudos: [2000, 6000],
} as const;

export interface MemoriaDoSinal {
    tempo?: Float32Array;
    frequencias?: Uint8Array;
}

export const SINAL_VAZIO: SinalDaVoz = { fonte: 'nenhuma', nivel: 0, graves: 0, medios: 0, agudos: 0 };

/** RMS em nível de 0 a 1: -55 dB é silêncio, -10 dB é voz forte perto do microfone. */
export function nivelDeRms(rms: number): number {
    if (!(rms > 0)) return 0;
    return Math.min(1, Math.max(0, (20 * Math.log10(rms) + 55) / 45));
}

/**
 * Energia média de uma faixa (0 a 1), com raiz para a fala comum não ficar apagada. A faixa é
 * meio aberta, [de, ate): a posição da fronteira (250 Hz, 2 kHz) conta numa faixa só.
 */
export function energiaDaFaixa(frequencias: ArrayLike<number>, hzPorPosicao: number, de: number, ate: number): number {
    const inicio = Math.max(1, Math.ceil(de / hzPorPosicao));
    const fim = Math.min(frequencias.length - 1, Math.ceil(ate / hzPorPosicao) - 1);
    if (fim < inicio) return 0;
    let soma = 0;
    for (let i = inicio; i <= fim; i += 1) soma += frequencias[i] ?? 0;
    return Math.min(1, Math.sqrt(soma / ((fim - inicio + 1) * 255)) * 1.25);
}

export function medirSinal(analisador: AnalyserNode, fonte: 'microfone' | 'voz', memoria: MemoriaDoSinal): SinalDaVoz {
    if (memoria.tempo?.length !== analisador.fftSize) memoria.tempo = new Float32Array(analisador.fftSize);
    if (memoria.frequencias?.length !== analisador.frequencyBinCount) memoria.frequencias = new Uint8Array(analisador.frequencyBinCount);
    const { tempo, frequencias } = memoria;
    analisador.getFloatTimeDomainData(tempo);
    analisador.getByteFrequencyData(frequencias);

    let soma = 0;
    for (const v of tempo) soma += v * v;
    const hzPorPosicao = analisador.context.sampleRate / analisador.fftSize;
    return {
        fonte,
        nivel: nivelDeRms(Math.sqrt(soma / tempo.length)),
        graves: energiaDaFaixa(frequencias, hzPorPosicao, ...FAIXAS.graves),
        medios: energiaDaFaixa(frequencias, hzPorPosicao, ...FAIXAS.medios),
        agudos: energiaDaFaixa(frequencias, hzPorPosicao, ...FAIXAS.agudos),
    };
}
